import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function PollOrderPanel() {
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .get("/admin/polls-order")
      .then((r) => {
        if (alive) setPolls(r.data);
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const persist = async (next) => {
    setPolls(next);
    setSaving(true);
    try {
      await api.put("/admin/polls-order", { order: next.map((p) => p.id) });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const move = (idx, delta) => {
    const target = idx + delta;
    if (target < 0 || target >= polls.length) return;
    const next = polls.slice();
    [next[idx], next[target]] = [next[target], next[idx]];
    persist(next);
  };

  const moveToTop = (idx) => {
    if (idx === 0) return;
    const next = polls.slice();
    const [item] = next.splice(idx, 1);
    next.unshift(item);
    persist(next);
  };

  const moveToBottom = (idx) => {
    if (idx === polls.length - 1) return;
    const next = polls.slice();
    const [item] = next.splice(idx, 1);
    next.push(item);
    persist(next);
  };

  return (
    <div className="border border-black">
      <div className="border-b border-black p-4">
        <div className="mono text-[10px] uppercase tracking-[0.3em]" style={{ color: "#fc4707" }}>
          Poll running order
        </div>
        <p className="text-sm text-neutral-700 mt-1">
          This is the order polls appear in the <span className="italic">Current Polls</span> sidebar on the front page.
          Newest polls appear last unless you move them.
          {saving && <span className="ml-3 mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Saving&hellip;</span>}
        </p>
      </div>
      {loading ? (
        <div className="p-8 mono text-xs text-neutral-500">Loading&hellip;</div>
      ) : polls.length === 0 ? (
        <div className="p-8 mono text-xs text-neutral-500">No approved polls yet.</div>
      ) : (
        <ol className="divide-y divide-neutral-200">
          {polls.map((p, idx) => (
            <li
              key={p.id}
              data-testid={`poll-order-row-${p.id}`}
              className="p-4 flex items-center gap-4 hover:bg-neutral-50"
            >
              <div className="mono text-xs uppercase tracking-[0.25em] text-neutral-500 w-8 tabular-nums shrink-0">
                {String(idx + 1).padStart(2, "0")}
              </div>
              <div className="flex-1 min-w-0">
                <div className="mono text-[10px] uppercase tracking-[0.25em] text-amber-700">
                  {(p.likert_statements && p.likert_statements.length > 0) ? "Likert poll" : "Poll"}
                  {p.topic ? <> &middot; {p.topic}</> : null}
                </div>
                <div className="serif text-lg mt-1 truncate">{p.title}</div>
              </div>
              <div className="shrink-0 flex gap-1">
                <button
                  type="button"
                  data-testid={`poll-order-top-${p.id}`}
                  onClick={() => moveToTop(idx)}
                  disabled={idx === 0 || saving}
                  title="Move to top"
                  className="border border-neutral-300 hover:border-black mono text-xs uppercase tracking-[0.2em] px-2 py-2 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  &uarr;&uarr;
                </button>
                <button
                  type="button"
                  data-testid={`poll-order-up-${p.id}`}
                  onClick={() => move(idx, -1)}
                  disabled={idx === 0 || saving}
                  title="Move up"
                  className="border border-neutral-300 hover:border-black mono text-xs uppercase tracking-[0.2em] px-3 py-2 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  &uarr;
                </button>
                <button
                  type="button"
                  data-testid={`poll-order-down-${p.id}`}
                  onClick={() => move(idx, 1)}
                  disabled={idx === polls.length - 1 || saving}
                  title="Move down"
                  className="border border-neutral-300 hover:border-black mono text-xs uppercase tracking-[0.2em] px-3 py-2 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  &darr;
                </button>
                <button
                  type="button"
                  data-testid={`poll-order-bottom-${p.id}`}
                  onClick={() => moveToBottom(idx)}
                  disabled={idx === polls.length - 1 || saving}
                  title="Move to bottom"
                  className="border border-neutral-300 hover:border-black mono text-xs uppercase tracking-[0.2em] px-2 py-2 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  &darr;&darr;
                </button>
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
